import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck, CircleX } from 'lucide-react';
import { Fragment, useId, useState } from 'react';
import { Link } from 'react-router';
import { isApiError } from '../../api/client';
import { useApi } from '../../api/context';
import { useEligibleDeployments } from '../../api/queries';
import type { AttachmentSize, EligibleDeployment, Network } from '../../api/types';
import { Button, Dialog, ErrorMessage, Loading } from '../../components/ui';
import { strings } from '../../strings';
import { networkErrorInfo, providerLabel, SIZE_ORDER, SIZE_PREFIX, sizeOptionLabel } from '../shared';

const p = strings.networks.page;

type Outcome = { ok: true } | { ok: false; message: string };

/** The largest recommended size of a row's regions (they can differ). */
function recommendedOf(row: EligibleDeployment): AttachmentSize {
  let best: AttachmentSize = row.regions[0]?.recommendedSize ?? 'medium';
  for (const r of row.regions)
    if (SIZE_ORDER.indexOf(r.recommendedSize) > SIZE_ORDER.indexOf(best)) best = r.recommendedSize;
  return best;
}

function Row({
  row,
  selected,
  size,
  outcome,
  busy,
  onSelect,
  onSize,
}: {
  row: EligibleDeployment;
  selected: boolean;
  size: AttachmentSize;
  outcome: Outcome | undefined;
  busy: boolean;
  onSelect: (selected: boolean) => void;
  onSize: (size: AttachmentSize) => void;
}) {
  const checkId = useId();
  const sizeId = useId();
  const reasonId = useId();
  const outcomeId = useId();
  const disabled = !row.canAttach || outcome?.ok === true;
  const recommended = recommendedOf(row);
  const describedBy = [!row.canAttach && row.reason ? reasonId : null, outcome ? outcomeId : null]
    .filter(Boolean)
    .join(' ');
  return (
    <li
      className={`net-attach-row ${row.canAttach ? '' : 'is-unavailable'}`}
      data-testid={`eligible-${row.deploymentName}`}
    >
      <input
        id={checkId}
        type="checkbox"
        className="mt-1"
        checked={selected}
        aria-disabled={disabled || busy ? true : undefined}
        aria-describedby={describedBy || undefined}
        aria-label={p.attachSelect(row.deploymentName)}
        onChange={(e) => {
          if (disabled || busy) return;
          onSelect(e.target.checked);
        }}
      />
      <div className="min-w-0">
        <label htmlFor={checkId} className="font-mono font-semibold">
          {row.deploymentName}
        </label>{' '}
        <span className="text-[12px] text-fg-muted">{row.blueprintName}</span>
        <ul className="mt-0.5 text-[12px]">
          {row.regions.map((r) => (
            <li key={`${r.provider}/${r.region}`} className="font-mono">
              {size !== r.size || !r.cidr
                ? `${providerLabel(r.provider).toLowerCase()} · ${r.region} ${p.assignedWhenSaved(SIZE_PREFIX[size])}`
                : p.blockLine(providerLabel(r.provider).toLowerCase(), r.region, r.cidr)}
            </li>
          ))}
        </ul>
        {row.impact && <p className="text-[12px] text-fg-muted">{row.impact}</p>}
        {!row.canAttach && row.reason && (
          <p id={reasonId} className="text-[12px] text-fg-muted">
            {row.reason}
          </p>
        )}
        {outcome && (
          <p
            id={outcomeId}
            className={`mt-1 flex items-center gap-1 text-[12px] ${outcome.ok ? 'text-success' : 'text-danger'}`}
            data-testid={`outcome-${row.deploymentName}`}
          >
            {outcome.ok ? (
              <CircleCheck aria-hidden className="size-3.5" />
            ) : (
              <CircleX aria-hidden className="size-3.5" />
            )}
            {outcome.ok ? p.attached : outcome.message}
          </p>
        )}
      </div>
      <div>
        <label htmlFor={sizeId} className="sr-only">
          {p.sizeFor(row.deploymentName)}
        </label>
        <select
          id={sizeId}
          className="input"
          value={size}
          aria-disabled={disabled || busy ? true : undefined}
          aria-describedby={!row.canAttach && row.reason ? reasonId : undefined}
          onChange={(e) => {
            if (disabled || busy) return;
            onSize(e.target.value as AttachmentSize);
          }}
        >
          {SIZE_ORDER.map((s) => (
            <option key={s} value={s}>
              {sizeOptionLabel(s)}
              {s === recommended ? ` · ${strings.networks.recommended}` : ''}
            </option>
          ))}
        </select>
      </div>
    </li>
  );
}

/**
 * Attach deployments… (spec 11 6.15.6): the eligible deployments with the block each would get and a size (the
 * recommended one chosen); Attach sends one deployment PATCH per selected row, one after another, so the setting's
 * single home stays the deployment.
 */
export function AttachDeploymentsDialog({ network, onClose }: { network: Network; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const eligible = useEligibleDeployments(network.id);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [sizes, setSizes] = useState<Record<string, AttachmentSize>>({});
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const [busy, setBusy] = useState(false);
  // Attached rows leave `eligible-deployments` when it is read again; they stay listed with their outcome.
  const [attached, setAttached] = useState<readonly EligibleDeployment[]>([]);
  const loaded = eligible.data ?? [];
  const rows = [...loaded, ...attached.filter((a) => !loaded.some((r) => r.deploymentId === a.deploymentId))];
  const sizeOf = (row: EligibleDeployment) => sizes[row.deploymentId] ?? recommendedOf(row);
  const chosen = rows.filter(
    (r) => selected.has(r.deploymentId) && r.canAttach && !outcomes[r.deploymentId]?.ok,
  );

  const attach = async () => {
    setBusy(true);
    for (const row of chosen) {
      let outcome: Outcome;
      try {
        const current = await api.getDeployment(row.deploymentId);
        await api.patchDeployment(
          row.deploymentId,
          { settings: { network: { networkId: network.id, size: sizeOf(row) } } },
          current.etag,
        );
        outcome = { ok: true };
      } catch (error) {
        const info = isApiError(error)
          ? networkErrorInfo(error.code, {
              network: network.name,
              deployment: row.deploymentName,
              message: error.detail,
            })
          : null;
        outcome = {
          ok: false,
          message: info?.sentence ?? (error instanceof Error ? error.message : String(error)),
        };
      }
      setOutcomes((o) => ({ ...o, [row.deploymentId]: outcome }));
      if (outcome.ok) setAttached((a) => [...a, row]);
      if (outcome.ok)
        setSelected((s) => {
          const next = new Set(s);
          next.delete(row.deploymentId);
          return next;
        });
      void queryClient.invalidateQueries({ queryKey: ['deployment', row.deploymentId] });
    }
    void queryClient.invalidateQueries({ queryKey: ['network', network.id] });
    void queryClient.invalidateQueries({ queryKey: ['networks'] });
    setBusy(false);
  };

  return (
    <Dialog
      title={p.attachTitle(network.name)}
      onClose={onClose}
      wide
      className="dialog-sheet"
      footer={
        <>
          <Button onClick={onClose}>{strings.common.close}</Button>
          <Button
            variant="primary"
            aria-disabled={chosen.length === 0 || busy ? true : undefined}
            onClick={() => {
              if (chosen.length === 0 || busy) return;
              void attach();
            }}
          >
            {busy ? p.attaching : p.attachSubmit(chosen.length)}
          </Button>
        </>
      }
    >
      {eligible.error ? (
        <ErrorMessage error={eligible.error} onRetry={() => void eligible.refetch()} />
      ) : !eligible.data ? (
        <Loading />
      ) : rows.length === 0 ? (
        <p className="help">{p.attachEmpty(network.name)}</p>
      ) : (
        <ul className="net-attach-list" aria-label={p.eligibleLabel}>
          {rows.map((row) => (
            <Row
              key={row.deploymentId}
              row={row}
              selected={selected.has(row.deploymentId)}
              size={sizeOf(row)}
              outcome={outcomes[row.deploymentId]}
              busy={busy}
              onSelect={(on) =>
                setSelected((s) => {
                  const next = new Set(s);
                  if (on) next.add(row.deploymentId);
                  else next.delete(row.deploymentId);
                  return next;
                })
              }
              onSize={(size) => setSizes((s) => ({ ...s, [row.deploymentId]: size }))}
            />
          ))}
        </ul>
      )}
      {attached.length > 0 && (
        <p className="callout callout-info mt-3" role="status" data-testid="attach-pending">
          <span>
            {p.pendingApply}{' '}
            {attached.map((row, i) => (
              <Fragment key={row.deploymentId}>
                {i > 0 && ', '}
                <Link className="link" to={`/deployments/${row.deploymentId}`}>
                  {row.deploymentName}
                </Link>
              </Fragment>
            ))}{' '}
            {p.pendingApplyEnd}
          </span>
        </p>
      )}
    </Dialog>
  );
}
